'use client';

import { useState, useEffect } from 'react';
import { Copy, Check, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { jsBeautifierContent } from '../../data/js-beautifier-content';
import Link from 'next/link';

export default function JsBeautifier() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const [indent, setIndent] = useState(2);

    useEffect(() => {
        const savedInput = localStorage.getItem('jsBeautifierInput');
        if (savedInput) {
            setInput(savedInput);
        }
    }, []);

    const formatJs = () => {
        try {
            if (!input.trim()) {
                setError('Please enter some JavaScript to format');
                return;
            }

            // Basic JavaScript formatting logic
            let formatted = input;

            // Add newlines after semicolons and braces
            formatted = formatted.replace(/;/g, ';\n');
            formatted = formatted.replace(/\{/g, ' {\n');
            formatted = formatted.replace(/\}/g, '\n}\n');

            // Remove multiple newlines
            formatted = formatted.replace(/\n{3,}/g, '\n\n');

            // Apply indentation
            const lines = formatted.split('\n');
            let indentLevel = 0;
            const indentedLines = lines.map(line => {
                const trimmed = line.trim();
                if (!trimmed) return '';

                if (trimmed.includes('}')) {
                    indentLevel = Math.max(0, indentLevel - 1);
                }

                const indentedLine = '  '.repeat(indentLevel) + trimmed;

                if (trimmed.includes('{')) {
                    indentLevel++;
                }

                return indentedLine;
            });

            formatted = indentedLines.join('\n');

            // Clean up extra spaces
            formatted = formatted.replace(/\s*;\s*/g, ';\n');
            formatted = formatted.replace(/\s*\{\s*/g, ' {\n');
            formatted = formatted.replace(/\s*\}\s*/g, '\n}\n');

            setOutput(formatted);
            setError('');

            localStorage.setItem('jsBeautifierInput', input);
            toast.success('JavaScript formatted successfully!');
        } catch (err) {
            setError('Error formatting JavaScript: ' + (err as Error).message);
            setOutput('');
        }
    };

    const copyToClipboard = () => {
        if (!output) return;

        navigator.clipboard.writeText(output).then(() => {
            setCopied(true);
            toast.success('Copied to clipboard!');
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const clearAll = () => {
        setInput('');
        setOutput('');
        setError('');
        localStorage.removeItem('jsBeautifierInput');
    };

    return (
        <div className="min-h-screen bg-[#F9FAFB] py-10 text-slate-900 dark:bg-slate-950 dark:text-slate-50">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                {/* Page Header */}
                <div className="mb-8 text-center">
                    <ToolPageIntro title="JavaScript Beautifier">
                Format and beautify JavaScript code with proper indentation and syntax highlighting. Our free JS beautifier makes your code readable and well-structured.
            </ToolPageIntro>
                </div>

                {/* Main tool card – full width */}
                <div className="space-y-6">
                    {/* Tool Interface */}
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="border-b border-slate-200 bg-slate-50/80 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/80">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="text-left">
                                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                                        Format your JavaScript
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        Paste JS on the left, get clean, formatted output on the right.
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <select
                                        value={indent}
                                        onChange={(e) => setIndent(Number(e.target.value))}
                                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition-colors focus:border-[#4F46E5] focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                                    >
                                        <option value={2}>2 spaces</option>
                                        <option value={4}>4 spaces</option>
                                        <option value={8}>8 spaces</option>
                                    </select>
                                    <button
                                        onClick={clearAll}
                                        className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                                    >
                                        Clear all
                                    </button>
                                    <button
                                        onClick={formatJs}
                                        className="inline-flex items-center rounded-full bg-[#4F46E5] px-4 py-1.5 text-xs font-semibold text-white shadow-sm shadow-indigo-500/30 transition hover:bg-[#4338CA] hover:shadow-md"
                                    >
                                        Format JS
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 divide-x divide-slate-200 dark:divide-slate-800 lg:grid-cols-2">
                            {/* Input Section */}
                            <div className="p-4">
                                <div className="mb-3 flex items-center justify-between">
                                    <h3 className="text-sm font-medium text-slate-900 dark:text-slate-50">
                                        Input JavaScript
                                    </h3>
                                </div>
                                <div className="relative">
                                    <textarea
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        className="h-96 w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-mono text-slate-900 placeholder-slate-400 shadow-sm transition-colors focus:border-[#4F46E5] focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500"
                                        placeholder="Paste your JavaScript code here..."
                                        spellCheck={false}
                                    />
                                </div>
                            </div>

                            {/* Output Section */}
                            <div className="p-4 bg-slate-50/50 dark:bg-slate-900/50">
                                <div className="mb-3 flex items-center justify-between">
                                    <h3 className="text-sm font-medium text-slate-900 dark:text-slate-50">
                                        Formatted JavaScript
                                    </h3>
                                    <button
                                        onClick={copyToClipboard}
                                        disabled={!output}
                                        className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition ${output
                                            ? 'border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                                            : 'cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500'
                                            }`}
                                    >
                                        {copied ? (
                                            <>
                                                <Check className="h-3.5 w-3.5" />
                                                Copied!
                                            </>
                                        ) : (
                                            <>
                                                <Copy className="h-3.5 w-3.5" />
                                                Copy
                                            </>
                                        )}
                                    </button>
                                </div>
                                <div className="relative">
                                    <textarea
                                        value={output}
                                        readOnly
                                        className="h-96 w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-mono text-slate-900 shadow-sm transition-colors dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                                        placeholder="Your formatted JavaScript will appear here..."
                                    />
                                </div>
                                {error && (
                                    <div className="mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800/30 dark:bg-red-900/20">
                                        <AlertCircle className="h-4 w-4 flex-shrink-0 text-red-500 dark:text-red-400" />
                                        <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Informational Content */}
                    <InformationalContent
                        title={jsBeautifierContent.title}
                        description={jsBeautifierContent.description}
                        content={jsBeautifierContent.content}
                        faqs={jsBeautifierContent.faqs}
                        relatedTools={jsBeautifierContent.relatedTools}
                    />
                </div>
            </div>
        </div>
    );
}
